// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/lobbyContent.js
//  MICROSERVICIO ONBOARDING v3 — Copy del Lobby de Bienvenida (Pantallas 0-0.2)
//
//  ⚠️  ORIGEN DEL COPY — LEER ANTES DE EDITAR:
//      A diferencia de onboardingContent.js (transcripción literal del documento
//      maestro), este archivo contiene copy NUEVO, redactado el 21/08/2026 por
//      encargo explícito del dueño del producto, que dio libre albedrío para
//      construir el enganche previo al diagnóstico.
//      ➜ PENDIENTE: incorporar estas 3 pantallas al documento maestro para que
//        vuelva a ser fuente única de verdad.
//
//  ── POR QUÉ EXISTE EL LOBBY ─────────────────────────────────────────────────
//      Arrancar el flujo pidiendo "¿cuál es tu gran objetivo?" a alguien que
//      acaba de aterrizar se siente intrusivo: se piden datos antes de haber
//      dado nada a cambio. El lobby invierte ese orden — primero NSL se
//      presenta y muestra su equipamiento, y solo entonces pide conocer al
//      usuario, ya con permiso ganado.
//
//  ── REGLA DE HONESTIDAD (Directiva Global v2.1/v2.2) ────────────────────────
//      Cero promesas de tiempo. Nada de "fluidez en 6 meses". El gancho es la
//      CAPACIDAD INSTALADA del gimnasio y el control que tiene el usuario sobre
//      su propio ritmo — nunca un resultado garantizado con fecha.
//
//      Toda tarjeta de EQUIPMENT corresponde a una función que EXISTE hoy en el
//      repo; el campo `evidence` documenta dónde vive cada una. Si una función
//      se retira del producto, su tarjeta se retira de aquí.
// ═══════════════════════════════════════════════════════════════════════════════

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 0 — El Lobby de Bienvenida (el enganche)
   ───────────────────────────────────────────────────────────────────────────── */

//  ── POR QUÉ ESTE COPY CAMBIÓ (24-09-2026) ──────────────────────────────────
//  La versión anterior decía «¡Hey, hola! / Bienvenido a / [logo] / Tu gimnasio
//  inteligente para entrenar tu inglés a tu propio ritmo». Es decir: saludaba,
//  daba su nombre y describía la categoría — pero no hacía ninguna promesa, y
//  esta pantalla es «/», la puerta de entrada de NSL. Rob lo resumió así:
//  «no impacta a nadie».
//
//  Ahora el titular carga el posicionamiento entero —gimnasio, no curso— y el
//  subtítulo nombra el dolor exacto del alumno objetivo: sabe las reglas y aun
//  así se congela. Es el mismo texto que ya aprobó para la página de precios.
export const LOBBY_WELCOME = {
  // Chapa amarilla: dice en qué negocio estamos antes de que empiece a leer.
  chapa: '⚡ El gimnasio para tu inglés',
  greeting: '¡Hey, hola!',
  headline: 'Bienvenido a',
  // El logo se renderiza como imagen y sigue al tema (/logo-dark.png | light).
  brandAlt: 'Non Stop Learning',
  // El titular va partido porque la segunda mitad se pinta dentro de un bloque
  // de color: es el recurso que le da el golpe de vista a la página de
  // empresas, y aquí se usa con el amarillo de NSL.
  titularLinea: 'El inglés no se estudia.',
  titularBloque: 'Se entrena',
  subheadline:
    '¿Conoces las reglas pero te congelas al hablar? Aquí repites las situaciones que de verdad te cuestan, hasta que dejan de costarte. A tu ritmo y a la hora que quieras.',
  cta: 'ME INTERESA',
  // Capacidad instalada, no promesas de resultado (regla de honestidad): las
  // tres corresponden a funciones que existen hoy, detalladas en
  // LOBBY_EQUIPMENT más abajo.
  pruebas: [
    { icon: '🕐', text: 'Abierto 24/7' },
    { icon: '🤖', text: 'Tutor de IA + teachers humanos' },
    { icon: '🪜', text: 'De cero absoluto a C1' },
  ],
};

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 0.1 — El Equipamiento (qué ofrecemos)
   ───────────────────────────────────────────────────────────────────────────── */

export const LOBBY_EQUIPMENT_INTRO = {
  eyebrow: 'El equipamiento',
  // Partido igual que el de la portada: la segunda mitad se pinta dentro del
  // bloque de color, que es lo que convierte un titular en un cartel.
  headline: 'Un gimnasio inteligente, con entrenamiento de',
  headlineBloque: 'alto impacto',
  subheadline:
    'Aquí no vienes a ver clases. Vienes a poner a prueba tu inglés en las situaciones que de verdad te cuestan — y a repetirlas hasta que dejen de costarte.',
};

/**
 * Cada tarjeta = una función real del producto.
 * `evidence` no se muestra al usuario: es la trazabilidad que sostiene la
 * promesa "no vendemos humo". Documenta dónde vive esa función en el repo.
 */
export const LOBBY_EQUIPMENT = [
  {
    icon: '🤖',
    title: 'Tutor IA Spacy',
    text: 'Tu entrenador personal, disponible a las 3 de la mañana si así lo decides. Conversa sin miedo a equivocarte.',
    evidence: 'src/pages/TutorPage.jsx, SpacyTutorPage.jsx, SpacyInLesson.jsx',
  },
  {
    icon: '🎭',
    title: 'Simuladores de situaciones reales',
    text: 'Entrevistas, viajes, reuniones, presentaciones. Practica el momento antes del momento.',
    evidence: 'backend-api/src/routers/adventure_routes.py (AI Adventure Mode)',
  },
  {
    icon: '👨‍🏫',
    title: 'Teachers humanos One-to-One',
    text: 'Porque hay cosas que solo detecta un ojo humano. Tu teacher asignado, tus sesiones agendadas.',
    evidence: 'models.TeacherAssignment + ClassSession, calendario_routes.py',
  },
  {
    icon: '🎮',
    title: 'Mini games',
    text: 'Diez juegos para que la gramática deje de sentirse como gramática.',
    evidence: 'src/minigames/ (Basta, PiensaRapido, NounsInvasion, TimeTraveler…)',
  },
  {
    icon: '🧠',
    title: 'Neuro-desafíos adaptativos',
    text: 'Nueve motores que generan tu reto del día según tu nivel real, no según un temario fijo.',
    evidence: 'src/engines/ + backend-api/src/routers/engines/engine_routes.py',
  },
  {
    icon: '🎧',
    title: 'Listening training',
    text: 'Del inglés de libro al inglés de la calle: acentos, velocidad y sarcasmo incluidos.',
    evidence: 'src/exercises/ListeningExercise.jsx',
  },
  {
    icon: '🎵',
    title: 'Lyrics training',
    text: 'Entrena el oído con la música que ya escuchas. Sí, esa que traes en repeat.',
    evidence: 'models.CuratedSong, lyrics_routes.py, MusicPlayerV2.jsx',
  },
  {
    icon: '📖',
    title: 'Glosario vivo',
    text: 'Cada palabra que te tropieza se queda guardada, y vuelve hasta que la domines.',
    evidence: 'src/pages/GlossaryPage.jsx, models.GlossaryWord',
  },
  {
    icon: '📊',
    title: 'Dashboard de progreso',
    text: 'Números reales de tu avance. Sin medallitas de participación.',
    evidence: 'src/pages/DashboardPage.jsx, models.EngineProgress + UserProgress',
  },
  {
    icon: '🪜',
    title: 'Todos los niveles',
    text: 'De cero absoluto a C1. El plan se ajusta a ti — nunca al revés.',
    evidence: 'models.Level (A1 → C1) + calibración adaptativa del diagnóstico',
  },
];

// Cierre anti-humo. Es el corazón del posicionamiento, no un adorno.
export const LOBBY_EQUIPMENT_CLOSING =
  'Nadie aquí te va a prometer fluidez en seis meses — eso no se puede garantizar y quien lo promete te está vendiendo algo. Lo que sí garantizamos es el gimnasio abierto 24/7. El límite lo pones tú.';

export const LOBBY_EQUIPMENT_CTA = 'Me interesa';

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 0.2 — La Transición (permiso para conocerte)
   ───────────────────────────────────────────────────────────────────────────── */

export const LOBBY_HANDOFF = {
  eyebrow: 'Antes de empezar',
  headline: 'Queremos conocerte para adaptar tu entrenamiento',
  headlineBloque: 'a ti',
  subheadline:
    'Un entrenador que no te conoce te pone la rutina de otro. Vamos a hacerlo bien: unas preguntas rápidas y una calibración corta para saber de dónde partes.',
  // Expectativas explícitas: reduce el abandono en la primera pregunta porque
  // el usuario ya sabe en qué se está metiendo y que puede salirse.
  promises: [
    { icon: '⏱️', text: 'Toma unos 3 minutos' },
    { icon: '🔓', text: 'Sin registro ni tarjeta' },
    { icon: '🎯', text: 'Terminas con tu plan de entrenamiento' },
  ],
  cta: "Let's do it",
};
